import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { apiConfig } from "../../config";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import ApiBlog from "./api-blog";
import {
  IBlogsGetAllParams,
  IGetAllBlogCommentsParams,
  ISubmitCommentResponse,
} from "./interfaces/IBlogs";

export const useBlog = () => new ApiBlog(apiConfig());
export const useGetAllBlogsQuery = ({
  params,
  enabled = true,
}: {
  params: IBlogsGetAllParams | any;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useQuery(
    [...getKey(Keys.getAllBlogs), Object.keys(params), Object.values(params)],
    () => blog.getAllBlogs({ ...params }),
    {
      refetchOnWindowFocus: true,
      enabled: enabled,
    }
  );
};

export const useGetBlogDetailsQuery = (slug: string) => {
  const blog = useBlog();
  return useQuery(
    [...getKey(Keys.getBlogDetails), slug],
    () => blog.getBlogDetails(slug),
    {
      refetchOnWindowFocus: true,
      enabled: true,
    }
  );
};

export const useGetBlogsCategoriesQuery = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useQuery(
    [...getKey(Keys.GetBlogsCategories)],
    () => blog.getBlogCategories(),
    {
      refetchOnWindowFocus: true,
      enabled: enabled,
    }
  );
};

export const useGetAllBlogCommentsQuery = ({
  blogSlug,
  params,
  enabled = true,
}: {
  blogSlug: string;
  params: IGetAllBlogCommentsParams;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useQuery(
    [
      ...getKey(Keys.getAllBlogComments),
      blogSlug,
      Object.keys(params),
      Object.values(params),
    ],
    () => blog.getAllBlogComments(blogSlug, { ...params }),
    {
      refetchOnWindowFocus: true,
      enabled: enabled,
    }
  );
};
export const useGetAllBlogCommentsInfiniteQuery = ({
  blogSlug,
  params,
  enabled = true,
}: {
  blogSlug: string;
  params: IGetAllBlogCommentsParams;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useInfiniteQuery(
    [...getKey(Keys.getAllBlogComments), blogSlug, "InfiniteQuery"],
    ({ pageParam = { ...params, page: 1 } }) =>
      blog.getAllBlogComments(blogSlug, { ...pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.result.meta.current_page + 1;
        const totalPages = lastPage.result.meta.last_page;
        return nextPage <= totalPages ? { ...params, page: nextPage } : null;
      },
      enabled: enabled,
    }
  );
};

export const useGetAllBlogCommentRepliesQuery = ({
  blogSlug,
  commentID,
  params,
  enabled = true,
}: {
  blogSlug: string;
  commentID: number;
  params: IGetAllBlogCommentsParams | any;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useQuery(
    [
      ...getKey(Keys.getAllBlogCommentReplies),
      blogSlug,
      Object.keys(params),
      Object.values(params),
    ],
    () => blog.getAllBlogCommentReplies(blogSlug, commentID, { ...params }),
    {
      refetchOnWindowFocus: true,
      enabled: enabled,
    }
  );
};

export const useGetAllBlogCommentRepliesInfiniteQuery = ({
  blogSlug,
  commentID,
  params,
  enabled = false,
}: {
  blogSlug: string;
  commentID: number;
  params: IGetAllBlogCommentsParams | any;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useInfiniteQuery(
    [
      ...getKey(Keys.getAllBlogCommentReplies),
      blogSlug,
      commentID,
      "InfiniteQuery",
    ],
    ({ pageParam = { ...params, page: 1 } }) =>
      blog.getAllBlogCommentReplies(blogSlug, commentID, { ...pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.result.meta.current_page + 1;
        const totalPages = lastPage.result.meta.last_page;
        return nextPage <= totalPages ? { ...params, page: nextPage } : null;
      },
      enabled: enabled,
    }
  );
};

export const useSubmitCommentQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: ISubmitCommentResponse) => void;
  onError: (err: any) => void;
}) => {
  const blog = useBlog();
  return useMutation(blog.submitComment, {
    onSuccess: (res: ISubmitCommentResponse) => {
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useBlogCommmentLikeToggleQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const blog = useBlog();
  return useMutation(blog.postCommentLikeToggle, {
    onSuccess: (res: any) => {
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useBlogCommmentDislikeToggleQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const blog = useBlog();
  return useMutation(blog.postCommentDislikeToggle, {
    onSuccess: (res: any) => {
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useSubscribeBlogsQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const blog = useBlog();
  return useMutation(blog.subscribeBlogs, {
    onSuccess: (res: any) => {
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useGetBlogLikeToggleQuery = ({
  blogSlug,
  enabled = false,
}: {
  blogSlug: string;
  enabled?: boolean;
}) => {
  const blog = useBlog();
  return useQuery(
    [...getKey(Keys.getBlogCommentDislikeToggle), blogSlug],
    () => blog.getBlogLikeToggle(blogSlug),
    {
      refetchOnWindowFocus: true,
      enabled: enabled,
    }
  );
};
export const usePostBlogReadingTimeQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const blog = useBlog();
  return useMutation(blog.postBlogReadingTime, {
    onSuccess: (res: any) => {
      onSuccess(res);
    },
    onError: onError,
  });
};
